import qs from 'qs';
import moment from 'moment';
import api from './api';
import generateSearchRequest from '../utils/sequelize-search-builder-client';

const selectSubscription = (subscription) => {
	return {
		...subscription,
		createdAt: subscription.createdAt ? moment(subscription.createdAt).toDate() : null,
		updatedAt: subscription.updatedAt ? moment(subscription.updatedAt).toDate() : null,
		startingDate: subscription.startingDate ? moment(subscription.startingDate).toDate() : null,
		closingDate: subscription.closingDate ? moment(subscription.closingDate).toDate() : null,
	};
};

const selectSubscriptions = (subscriptions) => subscriptions.map(selectSubscription);

class SubscriptionService {
	async getAll({ page = 0, size = 10, order, filters }) {
		const settings = {
			where: {
				id: {
					as: 'id',
					type: 'like',
				},
				clientId: {
					as: 'clientId',
					type: 'equal',
				},
				clientName: {
					as: 'client.name',
					type: 'like',
				},
				planName: {
					as: 'plan.name',
					type: 'like',
				},
				planId: {
					as: 'planId',
					type: 'equal',
				},
				isActive: {
					as: 'isActive',
					type: 'equal',
				},
				hash: {
					as: 'hash',
					type: 'like',
				},
				providerKey: {
					as: 'providerKey',
					type: 'like',
				},
				providerName: {
					as: 'providerName',
					type: 'like',
				},
				creditcardToken: {
					as: 'creditcardToken',
					type: 'like',
				},
				status: {
					as: 'status',
					type: 'like',
				},
			},
			order,
		};
		const request = generateSearchRequest(filters, settings);
		return api
			.get('/subscriptions', {
				paramsSerializer: {
					serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
				},
				params: {
					page,
					size,
					...request,
				},
			})
			.then((response) => response.data)
			.then((data) => ({ ...data, body: selectSubscriptions(data.body) }))
			.catch((error) => {
				console.log(
					'fail when trying to get subscriptions in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async create(subscriptionForm) {
		return api
			.post(`/plans/${subscriptionForm.planId}/purchase`, subscriptionForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to create subscription in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async createPaymentToken(creditCardInfo) {
		return new Promise((resolve, reject) => {
			if (!window.Iugu) {
				reject(new Error(`Iugu.js not loaded`));
			}

			window.Iugu.createPaymentToken(
				{
					number: creditCardInfo.number,
					verification_value: creditCardInfo.verificationValue,
					month: creditCardInfo.month,
					year: creditCardInfo.year,
					first_name: creditCardInfo.firstName,
					last_name: creditCardInfo.lastName,
				},
				(response) => {
					if (response.errors) {
						console.log('createPaymentToken errors:', response.errors);
						reject(new Error(`It was no possible to create a payment token`));
					} else {
						resolve(response.id);
					}
				},
			);
		});
	}

	async getById(id) {
		return api
			.get(`/subscriptions/${id}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to get subscription by id in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getStatuses() {
		return api
			.get('/subscriptions/statuses')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get subscriptions statuses in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getInvoices(subscriptionId, { page = 0, size = 10 }) {
		return api
			.get(`/subscriptions/${subscriptionId}/invoices`, {
				params: {
					page,
					size,
				},
			})
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					'fail when trying to get invoices in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async activate(subscriptionId) {
		return api
			.post(`/subscriptions/${subscriptionId}/activate`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to activate subscription in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async suspend(subscriptionId) {
		return api
			.post(`/subscriptions/${subscriptionId}/suspend`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to suspend subscription in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async payInvoiceExternally(
		subscriptionId,
		invoiceId,
		externalPaymentId,
		externalPaymentDescription,
	) {
		return api
			.post(`/subscriptions/${subscriptionId}/invoices/${invoiceId}/pay-externally`, {
				externalPaymentId,
				externalPaymentDescription,
			})
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to pay invoice externally in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async createNewInvoice(invoiceForm) {
		return api
			.post('/invoices', invoiceForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying create a new invoice externally in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getProviderinfo(subscriptionId) {
		return api
			.get(`/subscriptions/${subscriptionId}/provider-info`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get provider info in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getSubscriptionByClientId(clientId) {
		return api
			.get(`subscriptions/clientId/${clientId}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get subscription by client id SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async updateSubscriptionIUGU({ subscriptionId, payableWith }) {
		return api
			.post('/subscriptions/iugu/update/subscription', { subscriptionId, payableWith })
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					'fail when trying update subscription IUGU in subscrpition service.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
